// 🔧 QrCard.js
// ✅ Purpose: Reusable QR card layout for individual car entry
// ✅ Features:
// - Renders full print layout for a single car
// - Shows QR code, event logo, background image
// - Includes category, description, owner name
// - Optionally shows sponsor logos if provided
// - Used by: qr_print.js

import React from "react";
import QRCode from "react-qr-code";
import { Link } from "react-router-dom";

// 🧱 Shared component
/**
 * ------------------------------------------------------------
 * QR CARD
 * ------------------------------------------------------------
 * Display options:
 * - Every existing item defaults to visible.
 * - Existing callers that do not provide cardOptions continue
 *   rendering exactly as they do today.
 * - qr_print.js can selectively hide individual card elements.
 */
const QrCard = ({
  car,
  voteUrl,
  eventImage,
  eventLogo,
  sponsorLogos = [],
  categoriesMap = {},
  eventName = "",
  cardOptions = {},
}) => {
  const topLogos =
    sponsorLogos.slice(0, 7);

  // =========================================================
  // CARD CONTENT VISIBILITY
  // =========================================================
  // Explicit false hides an item.
  // Missing options remain true for backward compatibility.
  // =========================================================

  const showQrCode =
    cardOptions.showQrCode !== false;

  const showLogo =
    cardOptions.showLogo !== false;

  const showCategory =
    cardOptions.showCategory !== false;

  const showEntry =
    cardOptions.showEntry !== false;

  const showOwner =
    cardOptions.showOwner !== false;

  const showSponsors =
    cardOptions.showSponsors !== false;
    
  return (
    <div
      className={`qr-card ${eventImage ? "print-with-bg" : ""}`}
      style={eventImage ? { '--print-bg-url': `url(${eventImage})` } : {}}
    >

      {/* =====================================================
          Row 1: QR Code | Event Logo
          Each half remains structurally available only when its
          corresponding option is selected.
      ===================================================== */}

      {(showQrCode || showLogo) && (
        <div className="qr-row">

          {showQrCode && (
            <div
              className={
                showLogo
                  ? "qr-cell-50 qr-qr-code"
                  : "qr-cell-100 qr-qr-code"
              }
            >
              <Link
                to={voteUrl}
                target="_blank"
              >
                <QRCode
                  value={voteUrl}
                  size={325}
                />
              </Link>
            </div>
          )}

          {showLogo && (
            <div
              className={
                showQrCode
                  ? "qr-cell-50 qr-event-logo"
                  : "qr-cell-100 qr-event-logo"
              }
            >
              {eventLogo && (
                <img
                  src={eventLogo}
                  alt="Event Logo"
                />
              )}
            </div>
          )}

        </div>
      )}

      {/* Row 2: Category */}

      {showCategory && (
        <div className="qr-row">
          <div className="qr-cell-100 qr-category">
            {categoriesMap[car.catid] || "Unknown"}
          </div>
        </div>
      )}

      {/* =====================================================
          Row 3: Entry | Owner
          If only one is selected, that item expands to full width.
      ===================================================== */}

      {(showEntry || showOwner) && (
        <div className="qr-row">

          {showEntry && (
            <div
              className={
                showOwner
                  ? "qr-cell-50 qr-description"
                  : "qr-cell-100 qr-description"
              }
            >
              {car.description}
            </div>
          )}

          {showOwner && (
            <div
              className={
                showEntry
                  ? "qr-cell-50 qr-owner"
                  : "qr-cell-100 qr-owner"
              }
            >
              <strong className="qr-owner-name">
                {car.ownerFirst}{" "}
                {car.ownerLast}
              </strong>
            </div>
          )}

        </div>
      )}

      {/* =====================================================
          Rows 4 + 5: Sponsors
          Purpose:
          - Show sponsor heading and logos in both screen preview
            and printed output.
          - Sponsor visibility still follows the showSponsors
            card-content option.
      ===================================================== */}

      {showSponsors &&
        sponsorLogos.length > 0 && (
          <>
            <div className="qr-row">
              <div className="qr-cell-100 qr-sponsor-label">
                {eventName
                  ? `${eventName} Sponsors`
                  : "Event Sponsors"}
              </div>
            </div>

            <div className="qr-row qr-footer">
              {topLogos.map(
                (logo, idx) => (
                  <img
                    key={idx}
                    src={logo.logoUrl}
                    alt={`Sponsor ${idx + 1}`}
                  />
                )
              )}
            </div>
          </>
        )}

    </div>
  );
};

export default QrCard;
